import {
  AllNodesValidationStates,
  ValidationState,
  ValidationType,
} from "@features/builder/graph/nodes/validation/types"
import { nodesValidationAtom } from "@features/builder/graph/nodes/validation/validationAtom"
import { getDefaultStore } from "jotai"
import { useImmerAtom } from "jotai-immer"
import { uniqBy } from "lodash"

export function useNodesValidation() {
  const [nodesValidation, updateNodesValidation] = useImmerAtom(nodesValidationAtom, {
    store: getDefaultStore(), // make sure that we are in global scope
  })

  const hasErrors = Object.values(nodesValidation).some((validations) =>
    Object.values(validations).some(({ status }) => status === "error"),
  )

  const isNodeValid = (nodeId: string, excludeValidationKey?: ValidationType): boolean => {
    const nodeValidation = nodesValidation[nodeId]

    if (!nodeValidation) {
      return true
    }

    const localValidation = structuredClone(nodeValidation)
    if (excludeValidationKey) {
      delete localValidation[excludeValidationKey]
    }

    for (const { status } of Object.values(localValidation)) {
      if (status === "error") return false
    }
    return true
  }

  const setNodeValidation = (
    nodeId: string,
    validationKey: ValidationType,
    state: ValidationState,
  ) => {
    updateNodesValidation((draft) => {
      if (!draft[nodeId]) {
        draft[nodeId] = {}
      }

      const existing = draft[nodeId][validationKey]
      // Avoid redundant atom writes for the same payload to prevent unnecessary rerenders.
      const isUnchanged =
        existing !== undefined &&
        existing.status === state.status &&
        existing.errors.length === state.errors.length &&
        existing.errors.every((error, index) => error.msg === state.errors[index]?.msg)
      if (isUnchanged) {
        return
      }

      draft[nodeId][validationKey] = state
    })
  }

  /**
   * @param nodeId - id of node with error to get
   * @param validationType
   */
  const getNodeValidation = (
    nodeId: string | undefined,
    validationType?: ValidationType,
  ): ValidationState | undefined => {
    if (!nodeId) return undefined

    const nodeValidation = nodesValidation[nodeId]
    if (!nodeValidation) return undefined

    // Return errors only for provided validationType
    if (validationType !== undefined) {
      return nodeValidation[validationType]
    }

    // Return all unique errors
    const mergedErrors = uniqBy(
      Object.values(nodeValidation).flatMap((state) => state.errors),
      (error) => error.msg,
    )
    return {
      status: mergedErrors.length ? "error" : "valid",
      errors: mergedErrors,
    }
  }

  const setNodesValidation = (results: AllNodesValidationStates) => {
    updateNodesValidation((draft) => {
      for (const [nodeId, validations] of Object.entries(results)) {
        if (!draft[nodeId]) {
          draft[nodeId] = {}
        }
        for (const [validationType, state] of Object.entries(validations) as [
          ValidationType,
          ValidationState,
        ][]) {
          const existing = draft[nodeId][validationType]
          const isUnchanged =
            existing !== undefined &&
            existing.status === state.status &&
            existing.errors.length === state.errors.length &&
            existing.errors.every((error, index) => error.msg === state.errors[index]?.msg)
          if (isUnchanged) continue

          draft[nodeId][validationType] = state
        }
      }
    })
  }

  const getValidationErrors = (validationType: ValidationType, nodeIds: string[]) => {
    const errors = nodeIds.flatMap(
      (nodeId) => getNodeValidation(nodeId, validationType)?.errors ?? [],
    )
    return uniqBy(errors, (error) => error.msg)
  }

  const removeNodeValidation = (nodeId: string) => {
    updateNodesValidation((draft) => {
      delete draft[nodeId]
    })
  }

  const clear = () => updateNodesValidation(() => ({}))

  return {
    /**
     * Checks if node is valid. Optional excluding validationKey from validation check.
     * This is useful for checking all other validation errors for current node.
     */
    isNodeValid,
    /** List of all nodes that were validated */
    nodesValidation,
    /** Whether any validation has an error status. */
    hasErrors,
    /** Sets validation state for a node and validationKey */
    setNodeValidation,
    /** Sets validation state for multiple nodes and validation keys */
    setNodesValidation,
    /** Returns merged ValidationState across all validation types for a node. If validationType is provided, returns only that type's state. */
    getNodeValidation,
    /** Returns merged ValidationState for one validation types across all nodes. */
    getValidationErrors,
    /** Removes all validation states for a node */
    removeNodeValidation,
    /** Clears all node validations */
    clear,
  }
}
